import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from "@sentry/react";
import './i18n'; // Importo prima di App per garantire che i18n sia inizializzato
import App from './App';
import './index.css';
import './styles/fjord.css';
import { initializeUiExperience, uiRecoveryHref } from './lib/uiExperience';
initializeUiExperience();
import { debugContext } from './lib/debugContext';
import { initializeApiMonitoring } from './lib/monitoredFetch';
import { installUnauthorizedResponseGuard } from './lib/authSession';

// Inizializza il sistema di debug
initializeApiMonitoring();
// Un 401 e' una sessione scaduta, non una risposta CRM vuota: vale anche per
// i fetch legacy che non usano apiRequest/apiClient.
installUnauthorizedResponseGuard();

// Installa gli override della console
const resetConsole = debugContext.installConsoleOverrides();

// Espone il debugContext e funzioni helper a livello globale per l'accesso in produzione
// @ts-ignore
window.debugContext = debugContext;
// @ts-ignore
window.showDebugConsole = () => {
  // Utilizza lo store della debug console per mostrarla
  const debugStore = (document.querySelector("#root") as any)?.__ZUSTAND__;
  if (debugStore && typeof debugStore.getState === 'function') {
    const state = debugStore.getState();
    if (state && typeof state.showConsole === 'function') {
      state.showConsole();
      return true;
    }
  }
  
  // Fallback: log diretto se non è possibile accedere allo store
  debugContext.logInfo('Tentativo di apertura Debug Console tramite window globale', {
    success: false,
    reason: 'Store non accessibile'
  }, { component: 'GlobalDebug' });
  
  return false;
};

// Aggiungi un listener globale per gli errori non catturati
window.addEventListener('error', (event) => {
  // Reload immediato per errori di caricamento chunk JS (deploy in corso)
  const msg = event.message || event.error?.message || event.error?.toString() || '';
  if (msg.includes('Failed to fetch dynamically imported module') || msg.includes('Loading chunk') || msg.includes('Loading CSS chunk') || msg.includes('ChunkLoadError')) {
    console.warn('[GlobalError] Chunk load failure detected, reloading...');
    window.location.reload();
    return;
  }

  debugContext.logError('Errore globale non catturato', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error ? (event.error.stack || event.error.toString()) : 'Dettagli non disponibili',
    timestamp: new Date().toISOString()
  }, { component: 'GlobalErrorHandler', reportToSentry: true });
});

// Aggiungi un listener globale per le promesse non gestite
window.addEventListener('unhandledrejection', (event) => {
  debugContext.logError('Promessa non gestita', {
    reason: event.reason ? (event.reason.stack || event.reason.toString()) : 'Dettagli non disponibili',
    timestamp: new Date().toISOString()
  }, { component: 'GlobalErrorHandler', reportToSentry: true });
});

// Tema unificato Experviser (Attio-inspired). I temi legacy sono parcheggiati in /settings/theme.
document.documentElement.setAttribute('data-theme', 'experviser');

// Log iniziale per verificare il funzionamento
debugContext.logInfo('Applicazione inizializzata', {
  time: new Date().toISOString(),
  environment: import.meta.env.MODE,
  userAgent: navigator.userAgent
}, { component: 'AppInit' });

// Inizializza Sentry per il monitoraggio degli errori
// Nota: in produzione, usare un DSN reale fornito da Sentry
Sentry.init({
  dsn: "", // Lasciato vuoto per evitare invio di dati in fase di sviluppo
  integrations: [],
  
  // Impostazioni consigliate per ambienti di produzione
  // tracesSampleRate: 1.0, // Cattura il 100% delle transazioni per le performance
  // normalizeDepth: 10, // Aumenta la profondità degli oggetti normalizzati
  
  // Ambiente condizionale basato sulla build
  environment: import.meta.env.MODE,
  
  // Disabilita in ambiente di sviluppo
  enabled: import.meta.env.MODE === 'production',
  
  // Filtra errori indesiderati
  beforeSend(event) {
    // Ignora errori delle estensioni del browser
    if (event.exception && event.exception.values && event.exception.values[0]) {
      const errorMsg = event.exception.values[0].value || '';
      if (errorMsg.includes('extension') || errorMsg.includes('Plugin')) {
        return null;
      }
    }
    return event;
  },
});;

// Theme Provider
import { ThemeProvider } from './components/layouts/ThemeProvider';

// Renderizza l'applicazione nel DOM
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary 
      fallback={
        <div className="error-boundary" role="alert">
          <h2>Si è verificato un errore</h2>
          <p>Si è verificato un errore imprevisto. Puoi riprovare oppure tornare alla pagina precedente.</p>
          <button onClick={() => window.location.reload()}>Ricarica ora</button>
          <button type="button" onClick={() => {
            if (window.history.length > 1) window.history.back();
            else window.location.assign('/');
          }}>Torna indietro</button>
          <p><a href={uiRecoveryHref()}>Torna al CRM</a></p>
        </div>
      }
      onError={(error: any) => {
        // Reload immediato per errori di caricamento chunk JS (deploy in corso)
        const msg = error?.message || error?.toString() || '';
        if (msg.includes('Failed to fetch dynamically imported module') || msg.includes('Loading chunk') || msg.includes('Loading CSS chunk')) {
          console.warn('[ErrorBoundary] Chunk load failure detected, reloading...');
          window.location.reload();
        }
      }}
    >
      <ThemeProvider defaultTheme="light" storageKey="experviser-theme">
        <App />
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
